import { useMediaQuery } from 'usehooks-ts';

// breakpoint values from https://tailwindcss.com/docs/responsive-design#overview
type Breakpoint = {
  min?: number;
  max?: number;
};

const breakpoints: Record<string, Breakpoint> = {
  mobile: { max: 639 },
  sm: { min: 640, max: 767 },
  md: { min: 768, max: 1023 },
  lg: { min: 1024, max: 1279 },
  xl: { min: 1280, max: 1535 },
  xxl: { min: 1536 },
};

const useBreakpointQuery = ({ min, max }: Breakpoint) => {
  const minWidth = min === undefined ? undefined : `(min-width: ${min}px)`;
  const maxWidth = max === undefined ? undefined : `(max-width: ${max}px)`;
  const mediaQuery = [minWidth, maxWidth]
    .filter((x) => x !== undefined)
    .join(' and ');

  return useMediaQuery(mediaQuery);
};

export const useBreakpoint = () => {
  const isMobile = useBreakpointQuery(breakpoints.mobile);

  const isSmall = useBreakpointQuery(breakpoints.sm);
  const isAtLeastSmall = useBreakpointQuery({ min: breakpoints.sm.min });

  const isMedium = useBreakpointQuery(breakpoints.md);
  const isAtLeastMedium = useBreakpointQuery({ min: breakpoints.md.min });

  const isLarge = useBreakpointQuery(breakpoints.lg);
  const isAtLeastLarge = useBreakpointQuery({ min: breakpoints.lg.min });

  const isXLarge = useBreakpointQuery(breakpoints.xl);
  const isAtLeastXLarge = useBreakpointQuery({ min: breakpoints.xl.min });

  const isXXLarge = useBreakpointQuery(breakpoints.xxl);
  const isAtLeastXXLarge = useBreakpointQuery({ min: breakpoints.xxl.min });

  const all = { isMobile, isSmall, isMedium, isLarge, isXLarge, isXXLarge };

  type AllKeys = keyof typeof all;

  const currentBreakpoint: AllKeys = Object.entries(all)
    .filter(([, v]) => v)
    // TODO: refactor without type assertion, or add comment
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    .map(([k]) => k as AllKeys)[0];

  return {
    isMobile,

    isSmall,
    isAtLeastSmall,

    isMedium,
    isAtLeastMedium,

    isLarge,
    isAtLeastLarge,

    isXLarge,
    isAtLeastXLarge,

    isXXLarge,
    isAtLeastXXLarge,

    currentBreakpoint,
  };
};
